/*
Copyright (C) 2017  Cloudbase Solutions SRL
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import React from "react";
import { Link } from "react-router";
import { observer } from "mobx-react";
import styled from "styled-components";
import autobind from "autobind-decorator";
import {
  ArrowRightLeft,
  Bot,
  ChevronDown,
  Cloud,
  FolderPlus,
  Plus,
  UserPlus,
  type LucideIcon,
} from "lucide-react";

import IconTile, { type IconTileTone } from "@src/components/ui/IconTile/IconTile";
import { ThemePalette } from "@src/components/Theme";
import userStore from "@src/stores/UserStore";
import configLoader from "@src/utils/Config";

import { navigationMenu } from "@src/constants";

/*
 * The page header's "New" menu: the primary button (Plus icon), and a menu of Ops-style rows, each
 * an icon tile, a title and one line of description.
 */
const ICON_MAP: Record<string, { icon: LucideIcon; tone?: IconTileTone }> = {
  replica: { icon: ArrowRightLeft, tone: "info" },
  migration: { icon: ArrowRightLeft, tone: "info" },
  endpoint: { icon: Cloud },
  minionPool: { icon: Bot },
  user: { icon: UserPlus },
  project: { icon: FolderPlus },
};

const Wrapper = styled.div<any>`
  position: relative;
`;
const Button = styled.button<{ open?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 34px;
  padding: 0 10px 0 12px;
  border: 1px solid transparent;
  border-radius: 6px;
  background: ${props => (props.open ? ThemePalette.primaryLight : ThemePalette.primary)};
  color: white;
  font: inherit;
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  white-space: nowrap;
  cursor: pointer;
  transition: background 150ms cubic-bezier(0.4, 0, 0.2, 1);
  &:hover {
    background: ${ThemePalette.primaryLight};
  }
  &:focus-visible {
    outline: 2px solid ${ThemePalette.accent};
    outline-offset: 2px;
  }
`;
const Label = styled.span``;
const List = styled.div<any>`
  position: absolute;
  right: 0;
  top: calc(100% + 6px);
  width: 320px;
  z-index: 50;
  padding: 6px;
  background: var(--t9-bg-surface);
  border: 1px solid ${ThemePalette.grayscale[0]};
  border-radius: 8px;
  box-shadow:
    0 10px 15px -3px rgb(0 0 0 / 0.1),
    0 4px 6px -4px rgb(0 0 0 / 0.1);
`;
const ListItem = styled(Link)`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 10px;
  border-radius: 6px;
  text-decoration: none;
  color: ${ThemePalette.black};
  cursor: pointer;
  &:hover,
  &:focus-visible {
    outline: none;
    background: ${ThemePalette.grayscale[7]};
  }
`;
const Content = styled.div<any>`
  min-width: 0;
  display: flex;
  flex-direction: column;
`;
const Title = styled.div<any>`
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
`;
const Description = styled.div<any>`
  font-size: 12px;
  line-height: 16px;
  color: ${ThemePalette.grayscale[5]};
`;

export type ItemType = {
  href?: string;
  iconName: keyof typeof ICON_MAP;
  title: string;
  description: string;
  value?: string;
  disabled?: boolean;
  requiresAdmin?: boolean;
};
type Props = {
  onChange: (item: ItemType) => void;
};
type State = {
  showDropdownList: boolean;
};
@observer
class NewItemDropdown extends React.Component<Props, State> {
  state = {
    showDropdownList: false,
  };

  itemMouseDown: boolean | undefined;

  buttonRef = React.createRef<HTMLButtonElement>();

  listRef = React.createRef<HTMLDivElement>();

  componentDidMount() {
    window.addEventListener("mousedown", this.handlePageClick, false);
    window.addEventListener("keydown", this.handleKeyDown, false);
  }

  componentWillUnmount() {
    window.removeEventListener("mousedown", this.handlePageClick, false);
    window.removeEventListener("keydown", this.handleKeyDown, false);
  }

  @autobind
  handlePageClick() {
    if (!this.itemMouseDown) {
      this.setState({ showDropdownList: false });
    }
  }

  @autobind
  handleKeyDown(e: KeyboardEvent) {
    if (!this.state.showDropdownList) return;
    if (e.key === "Escape") {
      this.setState({ showDropdownList: false });
      this.buttonRef.current?.focus();
      return;
    }
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    const rows = Array.from(
      this.listRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') || [],
    );
    if (!rows.length) return;
    e.preventDefault();
    const i = rows.indexOf(document.activeElement as HTMLElement);
    rows[(i + (e.key === "ArrowDown" ? 1 : -1) + rows.length) % rows.length].focus();
  }

  handleButtonClick() {
    this.setState(prev => ({ showDropdownList: !prev.showDropdownList }));
  }

  handleItemClick(item: ItemType) {
    this.setState({ showDropdownList: false });

    if (this.props.onChange) {
      this.props.onChange(item);
    }
  }

  renderList() {
    if (!this.state.showDropdownList) {
      return null;
    }

    const isAdmin = userStore.loggedUser ? userStore.loggedUser.isAdmin : false;
    const disabledPages = configLoader.config
      ? configLoader.config.disabledPages
      : [];
    const items: ItemType[] = [
      {
        title: "Transfer",
        href: "/wizard/migration",
        description: "Replicate or migrate VMs between two clouds",
        iconName: "replica",
      },
      {
        title: "Cloud endpoint",
        value: "endpoint",
        description: "Add the connection details for a cloud",
        iconName: "endpoint",
      },
      {
        title: "SwiftIQ Agent",
        value: "minionPool",
        description: "Helper machines that read and write disks",
        iconName: "minionPool",
      },
      {
        title: "User",
        value: "user",
        description: "Add a SwiftIQ user",
        iconName: "user",
        disabled: Boolean(
          navigationMenu.find(
            i =>
              i.value === "users" &&
              (disabledPages.find(p => p === "users") ||
                (i.requiresAdmin && !isAdmin)),
          ),
        ),
      },
      {
        title: "Project",
        value: "project",
        description: "Create a SwiftIQ project",
        iconName: "project",
        disabled: Boolean(
          navigationMenu.find(
            i =>
              i.value === "projects" &&
              (disabledPages.find(p => p === "users") ||
                (i.requiresAdmin && !isAdmin)),
          ),
        ),
      },
    ];

    return (
      <List ref={this.listRef} role="menu" aria-label="New" id="swift-new-menu">
        {items
          .filter(i =>
            i.disabled ? !i.disabled : i.requiresAdmin ? isAdmin : true,
          )
          .map(item => {
            const icon = ICON_MAP[item.iconName];
            return (
              <ListItem
                key={item.title}
                role="menuitem"
                onMouseDown={() => {
                  this.itemMouseDown = true;
                }}
                onMouseUp={() => {
                  this.itemMouseDown = false;
                }}
                to={item.href || "#"}
                onClick={(e: React.MouseEvent) => {
                  // Items without a page open a modal: stay where we are.
                  if (!item.href && e && e.preventDefault) e.preventDefault();
                  this.handleItemClick(item);
                }}
              >
                <IconTile icon={icon.icon} tone={icon.tone} size={36} />
                <Content>
                  <Title>{item.title}</Title>
                  <Description>{item.description}</Description>
                </Content>
              </ListItem>
            );
          })}
      </List>
    );
  }

  render() {
    const open = this.state.showDropdownList;
    return (
      <Wrapper>
        <Button
          ref={this.buttonRef}
          type="button"
          open={open}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-controls={open ? "swift-new-menu" : undefined}
          onMouseDown={() => {
            this.itemMouseDown = true;
          }}
          onMouseUp={() => {
            this.itemMouseDown = false;
          }}
          onClick={() => this.handleButtonClick()}
        >
          <Plus size={16} strokeWidth={2} aria-hidden="true" />
          <Label>New</Label>
          <ChevronDown size={14} strokeWidth={2} aria-hidden="true" style={{ opacity: 0.8 }} />
        </Button>
        {this.renderList()}
      </Wrapper>
    );
  }
}

export default NewItemDropdown;
